import {
  AlertTriangle,
  ArrowRight,
  Award,
  Banknote,
  BarChart3,
  Briefcase,
  Building2,
  Coins,
  Crown,
  Database,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  Search,
  Send,
  Shield,
  ShieldOff,
  Target,
  TrendingUp,
  Users,
  Video,
  Wand2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../contexts/LanguageContext";
import authService from "../../services/authService";
import invitationService from "../../services/invitationService";
import apiService from "../../shared/apiService";
import { User as UserType } from "../../types/auth";
import PageLayout from "../layout/PageLayout";
import SectionHeader from "../layout/SectionHeader";
import { AdminPanelSkeleton } from "../skeletons";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import EmptyState from "../ui/EmptyState";
import Pagination from "../ui/Pagination";
import Select from "../ui/Select";
import Tooltip from "../ui/Tooltip";

const KPI_COLORS: Record<
  string,
  {
    border: string;
    text: string;
    iconBg: string;
    iconText: string;
    hoverGlow: string;
  }
> = {
  blue: {
    border: "border-blue-200",
    text: "text-blue-700",
    iconBg: "bg-blue-100",
    iconText: "text-blue-600",
    hoverGlow: "hover:shadow-[0_4px_24px_rgba(59,130,246,0.18)]",
  },
  emerald: {
    border: "border-emerald-200",
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-600",
    hoverGlow: "hover:shadow-[0_4px_24px_rgba(16,185,129,0.18)]",
  },
  orange: {
    border: "border-orange-200",
    text: "text-orange-700",
    iconBg: "bg-orange-100",
    iconText: "text-orange-600",
    hoverGlow: "hover:shadow-[0_4px_24px_rgba(249,115,22,0.18)]",
  },
  purple: {
    border: "border-purple-200",
    text: "text-purple-700",
    iconBg: "bg-purple-100",
    iconText: "text-purple-600",
    hoverGlow: "hover:shadow-[0_4px_24px_rgba(168,85,247,0.18)]",
  },
};

interface PossibleDuplicate {
  id: number;
  name: string;
  email: string;
  organization_id: number | null;
  organization_name: string | null;
  matched_by: string[];
}

export const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["dashboard", "users", "audit"];
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return fromUrl && VALID_TABS.includes(fromUrl) ? fromUrl : "dashboard";
  });

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", activeTab);
        return next;
      },
      { replace: true },
    );
  }, [activeTab, setSearchParams]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState<UserType[]>([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [approvingUuid, setApprovingUuid] = useState<string | null>(null);
  const [pendingSearch, setPendingSearch] = useState("");
  // Possíveis contas duplicadas (mesmo telefone/nome) por id de usuário pendente — só aviso.
  const [duplicatesById, setDuplicatesById] = useState<
    Record<number, PossibleDuplicate[]>
  >({});
  const [invitingId, setInvitingId] = useState<number | null>(null);

  // Utilitário de promoção/rebaixamento de superusuário Virtus (make-admin /
  // remove-admin) — por ID, já que não existe endpoint de busca por email.
  const [adminLookupId, setAdminLookupId] = useState("");
  const [adminLookupLoading, setAdminLookupLoading] = useState(false);
  const [adminLookupResult, setAdminLookupResult] = useState<UserType | null>(
    null,
  );

  // Audit State
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const [auditTotal, setAuditTotal] = useState(0);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedClientCompanyId, setSelectedClientCompanyId] =
    useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const loadAdminData = async () => {
    try {
      setIsUsersLoading(true);
      const res = await authService.getPendingUsers();
      if (res.success) {
        setPendingUsers(res.users);
      }

      const compRes = await apiService.get("/api/admin/companies");
      if (compRes.success) {
        setCompanies(compRes.companies || []);
      }

      try {
        const dupRes = await apiService.get("/api/admin/pending-users");
        const map: Record<number, PossibleDuplicate[]> = {};
        for (const u of dupRes.users || []) {
          if (u.possible_duplicates?.length) map[u.id] = u.possible_duplicates;
        }
        setDuplicatesById(map);
      } catch (dupError) {
        console.error("Erro ao verificar possíveis duplicatas:", dupError);
      }
    } catch (error) {
      console.error("Erro ao carregar dados administrativos:", error);
    } finally {
      setIsUsersLoading(false);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin" || user?.is_admin) {
      loadAdminData();
    } else {
      setPageLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAuditLogs = async (
    offset = 0,
    companyId = selectedCompanyId,
    clientCompanyId = selectedClientCompanyId,
    userId = selectedUserId,
  ) => {
    try {
      setIsAuditLoading(true);
      let url = `/api/admin/audit?limit=20&offset=${offset}`;
      if (companyId) url += `&organization_id=${companyId}`;
      if (clientCompanyId) url += `&client_company_id=${clientCompanyId}`;
      if (userId) url += `&user_id=${userId}`;
      const res = await apiService.get(url);
      if (res.success) {
        setAuditLogs(res.logs || []);
        setAuditTotal(res.total || 0);
      }
    } catch (error) {
      console.error("Erro ao carregar audit logs:", error);
    } finally {
      setIsAuditLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === "audit") {
      loadAuditLogs(
        auditPage * 20,
        selectedCompanyId,
        selectedClientCompanyId,
        selectedUserId,
      );
    }
  }, [
    activeTab,
    auditPage,
    selectedCompanyId,
    selectedClientCompanyId,
    selectedUserId,
  ]);

  const handleApproveUser = async (uuid: string, name: string) => {
    try {
      const result = await Swal.fire({
        title: t("Aprovar usuário?"),
        text: t("Deseja conceder acesso para {name}?", { name }),
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#8b5cf6",
        cancelButtonColor: "#6b7280",
        confirmButtonText: t("Sim, aprovar"),
        cancelButtonText: t("Cancelar"),
      });

      if (result.isConfirmed) {
        setApprovingUuid(uuid);
        const res = await authService.approveUser(uuid);
        if (res.success) {
          Swal.fire(
            t("Aprovado!"),
            t("Usuário {name} agora tem acesso ao sistema.", { name }),
            "success",
          );
          loadAdminData();
        }
      }
    } catch (error: any) {
      Swal.fire(
        t("Erro"),
        error.message || t("Falha ao aprovar usuário"),
        "error",
      );
    } finally {
      setApprovingUuid(null);
    }
  };

  /**
   * Convida o usuário para uma organização em nome dela. Não vincula na hora: a pessoa só
   * entra quando aceitar o convite (regra: sem organização até cadastrar a própria empresa
   * ou aceitar um convite).
   */
  const handleInviteToOrganization = async (pUser: UserType) => {
    const escape = (v: string) =>
      v.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const orgOptions = [...companies]
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .map((c) => `<option value="${c.id}">${escape(String(c.name))}</option>`)
      .join("");

    const result = await Swal.fire({
      title: t("Convidar para organização"),
      html: `
        <p style="font-size:13px;color:#6b7280;margin-bottom:12px">
          ${escape(t("{email} recebe um convite por e-mail e só entra na organização ao aceitar.", { email: String(pUser.email) }))}
        </p>
        <select id="invite-org" class="swal2-select" style="width:80%;margin:6px auto;display:block">
          <option value="">${escape(t("Selecione a organização"))}</option>${orgOptions}
        </select>
        <select id="invite-role" class="swal2-select" style="width:80%;margin:6px auto;display:block">
          <option value="EDITOR">${escape(t("Editor"))}</option>
          <option value="ADMIN">${escape(t("Admin"))}</option>
        </select>`,
      showCancelButton: true,
      confirmButtonColor: "#8b5cf6",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("Enviar convite"),
      cancelButtonText: t("Cancelar"),
      preConfirm: () => {
        const orgId = (
          document.getElementById("invite-org") as HTMLSelectElement
        )?.value;
        const role = (
          document.getElementById("invite-role") as HTMLSelectElement
        )?.value;
        if (!orgId) {
          Swal.showValidationMessage(t("Selecione a organização"));
          return false;
        }
        return { orgId: Number(orgId), role };
      },
    });
    if (!result.isConfirmed || !result.value) return;

    try {
      setInvitingId(pUser.id);
      const res = await invitationService.inviteAsPlatformAdmin(
        result.value.orgId,
        String(pUser.email),
        result.value.role === "ADMIN" ? "ADMIN" : "EDITOR",
      );
      Swal.fire(
        t("Convite enviado"),
        res.email_sent
          ? t("{name} vai entrar na organização assim que aceitar o convite.", {
              name: pUser.name,
            })
          : t("Convite criado, mas o e-mail não pôde ser enviado."),
        res.email_sent ? "success" : "warning",
      );
    } catch (error: any) {
      Swal.fire(
        t("Erro"),
        error?.errorData?.error ||
          error.message ||
          t("Falha ao enviar convite"),
        "error",
      );
    } finally {
      setInvitingId(null);
    }
  };

  const handleAdminLookupAction = async (
    action: "make-admin" | "remove-admin",
  ) => {
    const id = Number(adminLookupId);
    if (!id) return;
    try {
      setAdminLookupLoading(true);
      const res = await apiService.post(`/api/auth/user/${id}/${action}`, {});
      if (res?.user) {
        setAdminLookupResult(res.user);
        Swal.fire(
          t("Sucesso"),
          action === "make-admin"
            ? t("{name} agora é superusuário Virtus.", { name: res.user.name })
            : t("Privilégios de admin removidos de {name}.", {
                name: res.user.name,
              }),
          "success",
        );
      }
    } catch (error: any) {
      Swal.fire(
        t("Erro"),
        error.message || t("Falha ao atualizar privilégios do usuário"),
        "error",
      );
    } finally {
      setAdminLookupLoading(false);
    }
  };

  const filteredPendingUsers = pendingUsers.filter((u) => {
    const q = pendingSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      (u as any).company?.toLowerCase?.().includes(q)
    );
  });

  // Verificar se o usuário é admin
  if (user?.role !== "admin" && !user?.is_admin) {
    return (
      <PageLayout
        title={t("Painel de Administração")}
        containerSize="full"
        showContainer
        padding="none"
        className="w-full px-2! py-2!"
      >
        <div className="w-full flex flex-col gap-2">
          <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
            <EmptyState
              icon={<Shield className="w-16 h-16 text-red-200" />}
              title={t("Acesso Negado")}
              description={t(
                "Você precisa ser administrador para acessar esta área.",
              )}
            />
          </div>
        </div>
      </PageLayout>
    );
  }

  const tabsConfig = [
    {
      id: "dashboard",
      label: t("Dashboard"),
      icon: <BarChart3 className="h-4 w-4" />,
    },
    {
      id: "users",
      label: t("Usuários"),
      icon: <Users className="h-4 w-4" />,
      badge: pendingUsers.length > 0 ? pendingUsers.length : undefined,
    },
    {
      id: "audit",
      label: t("Auditoria"),
      icon: <Shield className="h-4 w-4" />,
    },
  ];

  // Métricas agregadas a partir de /api/admin/companies (já carregado pra
  // auditoria) — substituem os KPIs antigos de "Usuários Total"/"Posts
  // Criados"/"Status do Sistema", que nunca eram preenchidos com dado real
  // (ficavam travados em 0 ou num texto fixo "Online").
  const totalUsersAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.users_count || 0),
    0,
  );
  const totalLeadsAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.campaign_leads || 0),
    0,
  );
  const totalPostsAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.posts_created || 0),
    0,
  );
  const totalClientsAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.clients_count || 0),
    0,
  );
  const totalContentAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.content_library_count || 0),
    0,
  );

  const totalPostsPublished = companies.reduce(
    (sum, c) => sum + (c.posts_published || 0),
    0,
  );
  const totalImagesAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.images_created || 0),
    0,
  );
  const totalVideosAcrossOrgs = companies.reduce(
    (sum, c) => sum + (c.videos_created || 0),
    0,
  );
  const totalCreditsConsumed = companies.reduce(
    (sum, c) => sum + (c.credits_consumed || 0),
    0,
  );
  const totalCreditsBalance = companies.reduce(
    (sum, c) => sum + (c.credits_balance || 0),
    0,
  );
  const totalFaturamento = companies.reduce(
    (sum, c) => sum + (c.faturamento || 0),
    0,
  );

  const renderDashboardTab = () => {
    const MetricCard = ({
      icon: Icon,
      color,
      label,
      value,
      onClick,
      sub,
    }: any) => {
      const c = KPI_COLORS[color] || KPI_COLORS.blue;
      return (
        <div
          onClick={onClick}
          className={`relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 flex flex-col gap-3 overflow-hidden group hover:-translate-y-0.5 ${c.border} ${c.hoverGlow} ${onClick ? "cursor-pointer" : ""}`}
        >
          <div
            className={`absolute top-0 left-0 right-0 h-[3px] ${c.iconBg}`}
          />
          <div className="flex items-start justify-between pt-0.5">
            <div className="flex flex-col min-w-0">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 truncate">
                {label}
              </p>
              <h3
                className={`text-2xl font-black tracking-tight leading-none tabular-nums ${c.text}`}
              >
                {value}
              </h3>
            </div>
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110 ${c.iconBg} ${c.iconText}`}
            >
              <Icon className="h-4 w-4" />
            </div>
          </div>
          {sub && (
            <div
              className={`flex items-center justify-between gap-1 text-[10px] font-semibold px-2 py-1.5 rounded-lg ${c.iconBg} ${c.iconText}`}
            >
              <span className="truncate">{sub}</span>
              <ArrowRight className="h-3 w-3 shrink-0 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="flex flex-col gap-4">
        {/* Cabeçalho da Aba */}
        <div className="flex items-center justify-between gap-3 flex-wrap h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
              {t("Dashboard Executivo")}
            </h2>
          </div>
        </div>

        {/* Card 1: Infraestrutura */}
        <div className="bg-white rounded-xl border border-purple-200 p-4 sm:p-5 shadow-xs">
          <SectionHeader
            icon={<Building2 className="h-5 w-5" />}
            title={t("Rede e Organizações")}
            subtitle={t(
              "Visão geral de infraestrutura e acessos da plataforma",
            )}
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <MetricCard
              icon={Building2}
              color="purple"
              label={t("Organizações")}
              value={String(companies.length)}
              onClick={() => navigate("/admin/companies")}
              sub={t("Ver todas")}
            />
            <MetricCard
              icon={Briefcase}
              color="emerald"
              label={t("Clientes / Tenants")}
              value={String(totalClientsAcrossOrgs)}
            />
            <MetricCard
              icon={Users}
              color="blue"
              label={t("Usuários Ativos")}
              value={String(totalUsersAcrossOrgs)}
            />
            <MetricCard
              icon={AlertTriangle}
              color="orange"
              label={t("Aprovações Pendentes")}
              value={String(pendingUsers.length)}
              onClick={
                pendingUsers.length > 0
                  ? () => setActiveTab("users")
                  : undefined
              }
              sub={pendingUsers.length > 0 ? t("Revisar agora") : undefined}
            />
          </div>
        </div>

        {/* Card 2: Conteúdo */}
        <div className="bg-white rounded-xl border border-purple-200 p-4 sm:p-5 shadow-xs">
          <SectionHeader
            icon={<Wand2 className="h-5 w-5" />}
            title={t("Produção de Conteúdo e IA")}
            subtitle={t("Volume de materiais gerados, mídia e publicações")}
          />
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
            <MetricCard
              icon={FileText}
              color="purple"
              label={t("Posts Gerados")}
              value={String(totalPostsAcrossOrgs)}
            />
            <MetricCard
              icon={Send}
              color="blue"
              label={t("Posts Publicados")}
              value={String(totalPostsPublished)}
            />
            <MetricCard
              icon={ImageIcon}
              color="emerald"
              label={t("Imagens Criadas")}
              value={String(totalImagesAcrossOrgs)}
            />
            <MetricCard
              icon={Video}
              color="orange"
              label={t("Vídeos Gerados")}
              value={String(totalVideosAcrossOrgs)}
            />
            <MetricCard
              icon={Database}
              color="purple"
              label={t("Itens na Biblioteca")}
              value={String(totalContentAcrossOrgs)}
            />
          </div>
        </div>

        {/* Card 3: Financeiro */}
        <div className="bg-white rounded-xl border border-purple-200 p-4 sm:p-5 shadow-xs">
          <SectionHeader
            icon={<TrendingUp className="h-5 w-5" />}
            title={t("Resultados e Financeiro")}
            subtitle={t(
              "Leads captados, consumo de créditos e faturamento estimado",
            )}
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <MetricCard
              icon={Target}
              color="emerald"
              label={t("Leads Captados")}
              value={String(totalLeadsAcrossOrgs)}
            />
            <MetricCard
              icon={Coins}
              color="orange"
              label={t("Créditos Consumidos")}
              value={String(totalCreditsConsumed)}
            />
            <MetricCard
              icon={Coins}
              color="blue"
              label={t("Créditos Disponíveis")}
              value={String(totalCreditsBalance)}
            />
            <MetricCard
              icon={Banknote}
              color="purple"
              label={t("Faturamento Estimado")}
              value={`R$ ${totalFaturamento.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderUsersTab = () => (
    <div className="flex flex-col gap-4">
      {/* Cabeçalho da Aba */}
      <div className="flex items-center justify-between gap-3 flex-wrap min-h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
            {t("Gestão de Acessos e Privilégios")}
          </h2>
        </div>
      </div>

      {/* Utilitário de superusuário Virtus — promover/rebaixar admin por ID */}
      <div className="bg-white rounded-xl border border-purple-200 p-4 sm:p-5 shadow-xs">
        <SectionHeader
          icon={<Crown className="h-5 w-5" />}
          title={t("Privilégios de Superusuário")}
          subtitle={t(
            "Conceda ou remova acesso administrativo por ID de usuário",
          )}
        />
        <div className="flex flex-row flex-wrap items-end gap-2">
          <div className="w-full sm:w-[180px] shrink-0">
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
              {t("ID do Usuário")}
            </label>
            <input
              type="number"
              placeholder={t("Ex: 42")}
              value={adminLookupId}
              onChange={(e) => {
                setAdminLookupId(e.target.value);
                setAdminLookupResult(null);
              }}
              className="w-full h-9 px-3 rounded-lg border border-purple-300 bg-white text-sm focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            className="rounded-lg flex-1 sm:flex-none whitespace-nowrap"
            disabled={!adminLookupId}
            isLoading={adminLookupLoading}
            onClick={() => handleAdminLookupAction("make-admin")}
            icon={<Crown className="h-3.5 w-3.5" />}
          >
            {t("Promover a Admin")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="rounded-lg flex-1 sm:flex-none whitespace-nowrap"
            disabled={!adminLookupId}
            isLoading={adminLookupLoading}
            onClick={() => handleAdminLookupAction("remove-admin")}
            icon={<ShieldOff className="h-3.5 w-3.5" />}
          >
            {t("Remover Admin")}
          </Button>
        </div>
        {adminLookupResult && (
          <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-purple-50 border border-purple-100">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {adminLookupResult.name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {adminLookupResult.email}
              </p>
            </div>
            <Badge
              variant={
                (adminLookupResult as any).is_admin ? "success" : "default"
              }
              size="sm"
            >
              {(adminLookupResult as any).is_admin
                ? t("Admin")
                : t("Sem privilégio")}
            </Badge>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-purple-200 p-4 sm:p-5 shadow-xs">
        <SectionHeader
          icon={<Users className="h-5 w-5" />}
          title={t("Usuários Aguardando Aprovação")}
          subtitle={t("Solicitações de acesso recebidas")}
          action={
            <Tooltip content={t("Atualizar Lista")}>
              <button
                onClick={loadAdminData}
                aria-label={t("Atualizar Lista")}
                className="p-2 rounded-lg border border-purple-200 bg-white text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-all"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isUsersLoading ? "animate-spin" : ""}`}
                />
              </button>
            </Tooltip>
          }
        />

        {pendingUsers.length > 0 && (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
            <input
              type="text"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              placeholder={t("Buscar por nome, email ou empresa...")}
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-purple-200 bg-white text-sm focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
            />
          </div>
        )}

        {isUsersLoading ? (
          <div className="bg-white rounded-xl border border-purple-100 shadow-xs flex flex-col overflow-hidden animate-pulse">
            <div className="divide-y divide-purple-50">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="py-4 px-4 sm:px-6 flex items-center gap-4 flex-wrap lg:flex-nowrap"
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-11 h-11 rounded-xl bg-purple-50 shrink-0" />
                    <div className="flex flex-col gap-2">
                      <div className="h-4 w-32 bg-gray-200 rounded-sm" />
                      <div className="h-3 w-40 bg-gray-100 rounded-sm" />
                    </div>
                  </div>
                  <div className="flex items-center gap-6 shrink-0 w-full lg:w-auto mt-3 lg:mt-0">
                    <div className="flex flex-col gap-2 shrink-0 w-28">
                      <div className="h-2 w-12 bg-gray-200 rounded-sm" />
                      <div className="h-3 w-20 bg-gray-100 rounded-sm" />
                    </div>
                    <div className="flex flex-col gap-2 shrink-0 w-28">
                      <div className="h-2 w-12 bg-gray-200 rounded-sm" />
                      <div className="h-3 w-20 bg-gray-100 rounded-sm" />
                    </div>
                    <div className="flex flex-col gap-2 shrink-0 w-28">
                      <div className="h-2 w-12 bg-gray-200 rounded-sm" />
                      <div className="h-3 w-20 bg-gray-100 rounded-sm" />
                    </div>
                  </div>
                  <div className="shrink-0 mt-3 lg:mt-0 w-full lg:w-auto">
                    <div className="h-10 w-24 bg-purple-100 rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : pendingUsers.length === 0 ? (
          <EmptyState
            icon={<Users className="w-16 h-16 text-gray-200" />}
            title={t("Nenhuma solicitação pendente")}
            description={t("Não há solicitações pendentes no momento.")}
          />
        ) : filteredPendingUsers.length === 0 ? (
          <EmptyState
            icon={<Search className="w-16 h-16 text-gray-200" />}
            title={t("Nenhum resultado")}
            description={t("Nenhuma solicitação corresponde à busca.")}
          />
        ) : (
          <div className="bg-white rounded-xl border border-purple-100 shadow-xs flex flex-col overflow-hidden">
            <div className="divide-y divide-purple-100">
              {filteredPendingUsers.map((pUser) => (
                <div
                  key={pUser.uuid || pUser.id}
                  className="group py-4 px-4 sm:px-6 hover:bg-purple-50/20 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap">
                    {/* Identidade */}
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="relative shrink-0">
                        <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shadow-xs transition-transform duration-300 group-hover:scale-[1.02]">
                          <span className="text-sm font-black text-purple-700">
                            {pUser.name?.charAt(0).toUpperCase() || "?"}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0 flex flex-col">
                        <span className="font-extrabold text-gray-900 tracking-tight text-[15px] truncate mb-1">
                          {pUser.name}
                        </span>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 min-w-0">
                          <span className="truncate">{pUser.email}</span>
                        </div>
                        {duplicatesById[pUser.id]?.map((d) => (
                          <span
                            key={d.id}
                            className="mt-1 text-[11px] font-semibold text-amber-700"
                          >
                            {t("Possível duplicata")}: {d.name} ({d.email})
                            {d.organization_name
                              ? ` · ${d.organization_name}`
                              : ""}{" "}
                            · {t("mesmo")}{" "}
                            {d.matched_by.map((m) => t(m)).join(" / ")}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Detalhes: Empresa e Telefone */}
                    <div className="flex items-center gap-6 shrink-0 w-full lg:w-auto mt-3 lg:mt-0">
                      {pUser.company && (
                        <div className="flex flex-col shrink-0 w-28">
                          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                            {t("Empresa")}
                          </span>
                          <span className="text-xs font-bold text-gray-600 truncate">
                            {pUser.company}
                          </span>
                        </div>
                      )}
                      {pUser.phone && (
                        <div className="flex flex-col shrink-0 w-28">
                          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                            {t("Telefone")}
                          </span>
                          <span className="text-xs font-bold text-gray-600 truncate">
                            {pUser.phone}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col shrink-0 w-28">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                          {t("Solicitado em")}
                        </span>
                        <span className="text-xs font-bold text-gray-600 truncate">
                          {pUser.created_at
                            ? new Date(pUser.created_at).toLocaleDateString(
                                "pt-BR",
                              )
                            : "—"}
                        </span>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex flex-wrap items-center gap-2 shrink-0 mt-3 lg:mt-0 w-full lg:w-auto">
                      <Button
                        variant="primary"
                        size="sm"
                        isLoading={
                          approvingUuid === (pUser.uuid || String(pUser.id))
                        }
                        onClick={() =>
                          handleApproveUser(
                            pUser.uuid || String(pUser.id),
                            pUser.name,
                          )
                        }
                        className="rounded-xl h-10 px-6 text-xs font-black uppercase tracking-widest shadow-lg shadow-purple-500/20 w-full lg:w-auto"
                      >
                        {t("Aprovar")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        isLoading={invitingId === pUser.id}
                        onClick={() => handleInviteToOrganization(pUser)}
                        className="rounded-xl h-10 px-4 text-xs font-black uppercase tracking-widest border-purple-200 w-full lg:w-auto"
                      >
                        {t("Convidar para organização")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const selectedCompany = companies.find(
    (c) => c.id === Number(selectedCompanyId),
  );

  const renderAuditTab = () => (
    <div className="flex flex-col gap-4">
      {/* Cabeçalho da Aba */}
      <div className="flex items-center justify-between gap-3 flex-wrap min-h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
            {t("Auditoria do Sistema")}
          </h2>
        </div>
        <Tooltip content={t("Atualizar")}>
          <button
            onClick={() => loadAuditLogs(auditPage * 20)}
            aria-label={t("Atualizar")}
            className="p-1.5 rounded-lg border border-purple-200 bg-white text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-all shadow-xs"
          >
            <RefreshCw
              className={`h-4 w-4 ${isAuditLoading ? "animate-spin" : ""}`}
            />
          </button>
        </Tooltip>
      </div>

      <div className="bg-white rounded-xl border border-purple-200 p-4 sm:p-5 shadow-xs">
        <SectionHeader
          icon={<Search className="h-5 w-5" />}
          title={t("Filtros de Busca")}
          subtitle={t("Encontre ações administrativas específicas")}
          hideBorder={true}
          action={
            <div className="flex flex-row flex-wrap gap-2 items-center justify-end">
              <div className="w-[300px]">
                <Select
                  value={selectedCompanyId}
                  onChange={(v) => {
                    setSelectedCompanyId(v);
                    setSelectedClientCompanyId("");
                    setAuditPage(0);
                  }}
                  className="h-10!"
                  options={[
                    { value: "", label: t("Todas as Organizações") },
                    ...companies.map((c) => ({
                      value: String(c.id),
                      label: c.name,
                    })),
                  ]}
                />
              </div>

              {selectedCompanyId && (
                <div className="w-[190px]">
                  <Select
                    value={selectedClientCompanyId}
                    onChange={(v) => {
                      setSelectedClientCompanyId(v);
                      setAuditPage(0);
                    }}
                    className="h-10!"
                    options={[
                      { value: "", label: t("Todos os Clientes") },
                      ...(selectedCompany?.client_companies?.map((cc: any) => ({
                        value: String(cc.id),
                        label: cc.name,
                      })) ?? []),
                    ]}
                  />
                </div>
              )}

              <div className="w-[140px]">
                <input
                  type="number"
                  placeholder={t("ID (User)")}
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    setAuditPage(0);
                  }}
                  className="w-full h-10 px-3.5 rounded-xl border border-purple-300 bg-white text-sm focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                />
              </div>
            </div>
          }
        />
      </div>

      {isAuditLoading ? (
        <div className="bg-white rounded-xl border border-purple-100 shadow-xs flex flex-col overflow-hidden animate-pulse">
          <div className="divide-y divide-purple-50">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="py-4 px-4 sm:px-6 flex items-center gap-4 flex-wrap lg:flex-nowrap"
              >
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                  <div className="flex items-center gap-2.5">
                    <div className="h-4 w-32 bg-gray-200 rounded-sm" />
                    <div className="h-5 w-16 bg-purple-50 rounded-full" />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="h-3 w-20 bg-gray-100 rounded-sm" />
                    <div className="h-3 w-20 bg-gray-100 rounded-sm" />
                    <div className="h-3 w-24 bg-gray-100 rounded-sm" />
                  </div>
                </div>
                <div className="shrink-0 w-full lg:w-[400px] mt-3 lg:mt-0">
                  <div className="h-16 w-full bg-gray-50 rounded-lg border border-purple-50" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : auditLogs.length === 0 ? (
        <EmptyState
          icon={<Shield className="w-16 h-16 text-gray-200" />}
          title={t("Nenhum log encontrado")}
          description={t("Nenhuma ação registrada para esses filtros.")}
        />
      ) : (
        <>
          <div className="bg-white rounded-xl border border-purple-100 shadow-xs flex flex-col overflow-hidden">
            <div className="divide-y divide-purple-100">
              {auditLogs.map((log) => (
                <div
                  key={log.id}
                  className="group py-4 px-4 sm:px-6 hover:bg-purple-50/20 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap">
                    <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1">
                        <span className="font-extrabold text-gray-900 tracking-tight text-[15px] truncate">
                          {log.user ? log.user.name : `ID: ${log.user_id}`}
                        </span>
                        <Badge
                          variant={
                            log.action.includes("APPROVE")
                              ? "success"
                              : log.action.includes("REJECT")
                                ? "destructive"
                                : "default"
                          }
                          size="sm"
                        >
                          {log.action}
                        </Badge>
                      </div>

                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex flex-wrap items-center gap-1.5 min-w-0">
                        <span className="truncate">
                          {log.entity_type} #{log.entity_id}
                        </span>
                        <span>•</span>
                        <span className="truncate">
                          {new Date(log.created_at).toLocaleString("pt-BR")}
                        </span>
                        <span>•</span>
                        <span className="truncate">IP: {log.ip_address}</span>
                      </div>
                    </div>

                    <div className="shrink-0 w-full lg:w-[400px] bg-gray-50 rounded-lg p-3 border border-purple-100 mt-3 lg:mt-0 max-h-32 overflow-y-auto scrollbar-thin">
                      <pre className="text-[11px] text-gray-600 font-mono whitespace-pre-wrap break-all leading-relaxed">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <Pagination
              currentPage={auditPage + 1}
              totalPages={Math.max(1, Math.ceil(auditTotal / 20))}
              totalItems={auditTotal}
              itemsPerPage={20}
              onPageChange={(p) => setAuditPage(p - 1)}
            />
          </div>
        </>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return renderDashboardTab();
      case "users":
        return renderUsersTab();
      case "audit":
        return renderAuditTab();
      default:
        return renderDashboardTab();
    }
  };

  return (
    <PageLayout
      title={t("Painel de Administração")}
      subtitle={t(
        "Métricas globais, aprovações de acesso e auditoria do sistema.",
      )}
      containerSize="full"
      showContainer
      padding="none"
      className="w-full px-2! py-2!"
      loading={pageLoading}
      skeleton={<AdminPanelSkeleton />}
      actions={
        <div className="hidden md:flex items-center gap-2 px-4 py-2 h-8 rounded-full text-[10px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-100">
          <Award className="h-3.5 w-3.5" />
          {t("Bem-vindo,")} {user?.name}
        </div>
      }
    >
      {/* Wrapper único agrupando navbar e conteúdo para controlar gap */}
      <div className="w-full">
        {/* Navbar de navegação - sticky header (padrão AutoResponder) */}
        <div className="sticky top-0 z-30 -mt-2 -mx-2 mb-4 bg-white/95 backdrop-blur-md border-b border-purple-200">
          <div className="w-full flex gap-1 bg-gray-100 p-1">
            {tabsConfig.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-bold uppercase tracking-wide transition-all duration-150 ${
                    isActive
                      ? "bg-purple-600 text-white shadow-xs"
                      : "bg-white text-gray-600 border border-gray-200 shadow-xs hover:text-purple-700 hover:border-purple-300 cursor-pointer"
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span
                      className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                        isActive
                          ? "bg-white/20 text-white"
                          : "bg-orange-100 text-orange-700"
                      }`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {renderTabContent()}
      </div>
    </PageLayout>
  );
};
